import axiosInstance from "../utils/axios";

export interface LoginWithEmailPayload {
  email: string;
  password: string;
  device_type?: string;
  device_token?: string;
}

export interface LoginResponse {
  status: boolean;
  message: string;
  data: {
    user: {
      _id: string;
      email: string;
      phone_number: string;
      full_name: string;
      profile_image?: string;
      status: boolean;
      created_at: string;
      updated_at: string;
    };
    token: string;
  };
}

export const getCountryListAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/country/list");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    throw new Error(error.message || "Failed to get country list");
  }
};

export const loginWithEmailAPI = async (payload: LoginWithEmailPayload): Promise<LoginResponse> => {
  try {
    const response = await axiosInstance.post("/api/user/auth/login-email", {
      email: payload.email.trim().toLowerCase(),
      password: payload.password,
      device_type: payload.device_type || "web",
      device_token: payload.device_token || localStorage.getItem("fcmToken") || "",
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = errors[0]?.msg || errors[0];
      throw new Error(firstError);
    }

    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Login failed. Please try again.");
  }
};

export const logoutAPI = async () => {
  try {
    const response = await axiosInstance.post("/api/user/auth/logout");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to logout");
  }
};

export const registerWithEmailAPI = async (payload: any) => {
  try {
    const formData = new FormData();
    formData.append("email", payload.email.trim().toLowerCase());
    formData.append("password", payload.password);
    formData.append("full_name", payload.full_name);
    if (payload.dob) {
      formData.append("dob", payload.dob);
    }
    if (payload.gender) {
      formData.append("gender", payload.gender);
    }
    if (payload.profile_image) {
      formData.append("profile_image", payload.profile_image);
    }
    formData.append("device_type", payload.device_type || "web");
    formData.append("device_token", payload.device_token || localStorage.getItem("fcmToken") || "");
    const response = await axiosInstance.post("/api/user/auth/register", formData);

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = errors[0]?.msg || errors[0];
      throw new Error(firstError);
    }

    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Login failed. Please try again.");
  }
};

export const sendOtpForLoginAPI = async (payload: { phone_number: string; country_code: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/send-otp-for-login", {
      phone_number: payload.phone_number.trim(),
      country_code: payload.country_code,
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to send OTP");
  }
};

export const verifyOtpAndLoginAPI = async (payload: {
  phone_number: string;
  otp: string;
  country_code?: string;
  device_type?: string;
  device_token?: string;
  email?: string;
  full_name?: string;
}) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/verify-otp-and-login", {
      phone_number: payload.phone_number.trim(),
      otp: payload.otp,
      country_code: payload.country_code || undefined,
      device_type: payload.device_type || "web",
      device_token: payload.device_token || localStorage.getItem("fcmToken") || "",
      email: payload.email ? payload.email.trim().toLowerCase() : undefined,
      full_name: payload.full_name || undefined,
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "OTP verification failed");
  }
};

export const sendOtpForRegisterAPI = async (payload: { phone_number: string; country_code: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/send-otp-for-register", {
      phone_number: payload.phone_number.trim(),
      country_code: payload.country_code,
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to send OTP");
  }
};

export const verifyOtpAndRegisterAPI = async (payload: {
  phone_number: string;
  otp: string;
  country_code?: string;
  device_type?: string;
  device_token?: string;
  email?: string;
  full_name?: string;
}) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/verify-otp-and-register", {
      phone_number: payload.phone_number.trim(),
      otp: payload.otp,
      country_code: payload.country_code || undefined,
      device_type: payload.device_type || "web",
      device_token: payload.device_token || localStorage.getItem("fcmToken") || "",
      email: payload.email ? payload.email.trim().toLowerCase() : undefined,
      full_name: payload.full_name || undefined,
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "OTP verification failed");
  }
};

// idToken: z.string().min(1, "idToken is required"),
//   device_type: z.string().optional(),
//   device_token: z.string().optional(),
//   country_code: z.string().optional(),
//   phone_number: z.string().optional(),

export const phoneLoginAPI = async (payload: {
  idToken: string;
  device_type?: string;
  device_token?: string;
  country_code?: string;
  phone_number?: string;
}) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/phone-login", {
      idToken: payload.idToken,
      device_type: payload.device_type || "web",
      device_token: payload.device_token || localStorage.getItem("fcmToken") || "",
      country_code: payload.country_code || undefined,
      phone_number: payload.phone_number || undefined,
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to login with phone");
  }
};

export const phoneRegisterAPI = async (payload: {
  idToken: string;
  device_type?: string;
  device_token?: string;
  country_code?: string;
  phone_number?: string;
  full_name?: string;
  dob?: string;
  gender?: string;
  profile_image?: string;
}) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/register-with-phone", {
      idToken: payload.idToken,
      device_type: payload.device_type || "web",
      device_token: payload.device_token || localStorage.getItem("fcmToken") || "",
      country_code: payload.country_code || undefined,
      phone_number: payload.phone_number || undefined,
      full_name: payload.full_name || undefined,
      dob: payload.dob || undefined,
      gender: payload.gender || undefined,
      profile_image: payload.profile_image || undefined,
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to register with phone");
  }
};

export const updateUserMobileNumberAPI = async (payload: { idToken: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/verify-otp-firebase", {
      idToken: payload.idToken,
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to register with phone");
  }
};

export const checkPhoneExistsAPI = async (payload: { phone_number: string; country_code: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/check-phone-exists", {
      phone_number: payload.phone_number.trim(),
      country_code: payload.country_code,
    });
    // console.log("Check Phone Exists Response:", response);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to check phone exists");
  }
};

export const checkPhoneExistsRegisterAPI = async (payload: { phone_number: string; country_code: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/check-phone-exists-register", {
      phone_number: payload.phone_number.trim(),
      country_code: payload.country_code,
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to check phone exists register");
  }
};

/** Profile update: check phone is not used by another account (requires auth). */
export const checkPhoneExistsUpdateAPI = async (payload: { phone_number: string; country_code: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/check-phone-exists-update", {
      phone_number: payload.phone_number.trim(),
      country_code: payload.country_code,
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to check phone exists");
  }
};

export const forgotPasswordAPI = async (email: string) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/forgot-password", {
      email: email.trim().toLowerCase(),
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to send reset link");
  }
};

export const resetPasswordAPI = async (payload: { token: string; new_password: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/reset-password", {
      token: payload.token,
      new_password: payload.new_password,
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to reset password");
  }
};

export const changePasswordAPI = async (payload: { old_password: string; new_password: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/change-password", payload);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to change password");
  }
};

export const deleteAccountAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/auth/delete-account");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to delete account");
  }
};

export const setAuthToken = (token: string, rememberMe: boolean = true) => {
  if (rememberMe) {
    localStorage.setItem("token", token);
  } else {
    sessionStorage.setItem("token", token);
  }
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

export const getCurrentToken = () => {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
};

export const isAuthenticated = () => {
  return !!getCurrentToken();
};

export const logout = () => {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
  delete axiosInstance.defaults.headers.common["Authorization"];
};

export interface SocialLoginPayload {
  email: string;
  social_type: "google" | "facebook" | "apple";
  social_id: string;
  full_name: string;
  device_type?: string;
  device_token?: string;
}

export const socialLoginAPI = async (payload: SocialLoginPayload): Promise<LoginResponse> => {
  try {
    const response = await axiosInstance.post("/api/user/auth/social-login", {
      email: (payload.email || "").trim().toLowerCase(),
      social_type: payload.social_type,
      social_id: payload.social_id,
      full_name: payload.full_name,
      device_type: payload.device_type || "web",
      device_token: payload.device_token || localStorage.getItem("fcmToken") || "",
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = errors[0]?.msg || errors[0];
      throw new Error(firstError);
    }

    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Social login failed. Please try again.");
  }
};

export const verifyEmailAPI = async (token: string) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/verify-email", {
      token: token,
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Email verification failed. Please try again.");
  }
};

export const verifyEmailUpdateAPI = async (token: string) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/verify-email-update", {
      token: token,
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Email verification failed. Please try again.");
  }
};

export const resendVerificationEmailAPI = async (email: string) => {
  try {
    const formData = new FormData();
    formData.append("email", email.trim().toLowerCase());

    const response = await axiosInstance.post("/api/user/auth/resend-verification-email", formData);

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Failed to resend verification email. Please try again.");
  }
};

export const sendEmailUpdateVerificationAPI = async (email: string) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/send-email-update-verification", {
      email: email.trim().toLowerCase(),
    });

    if (!response.data.status) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    if (error.message === "Network Error") {
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }

    throw new Error(error.message || "Failed to resend verification email. Please try again.");
  }
};
